import React, { ReactNode, useState } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { type CalendarDate, parseDate } from "@internationalized/date";
import {
  Button,
  DateField,
  IconButton,
  ItemsPerPage,
  Pagination,
  SingleSelectField,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TextField,
} from "@shoptet/ui";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";
import { AdSystemT, AuditLogsT, FeatureFlagT, useAuditLogsQuery } from "../../graphql/generated/graphql";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { formatTime } from "../../i18n/formatDate";
import { ImageSuggestion, UploadedImageSuggestion } from "../ImageAssetLines/ImageSuggestion";
import { SmallLoader } from "../SmallLoader";

const asRichText = <T extends ReactNode>(node: T) => node as T & { __brand: "RichText" };

enum SystemFilter {
  ALL = "all",
  GOOGLE = "google",
  META = "meta",
}

type ExtendedDateFieldProps = React.ComponentProps<typeof DateField> & {
  maxValue?: CalendarDate;
  minValue?: CalendarDate;
};

const ExtendedDateField = (props: ExtendedDateFieldProps) => <DateField {...props} />;

export const AuditLogs = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Audit Log Page" });
  const { t: tPagination } = useTranslation("translation", { keyPrefix: "Components.Pagination" });
  const { enabled: hasMetaCampaignsEnabled } = useFeatureFlag(FeatureFlagT.MetaCampaignsT);

  const savedItemsPerPage = parseInt(localStorage.getItem("shoptet_audit_logs_items_per_page") ?? "10");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<ItemsPerPage>(savedItemsPerPage as ItemsPerPage);

  // Applied filter states (used in query)
  const [appliedDateTo, setAppliedDateTo] = useState<string>(DateTime.local().toFormat("yyyy-MM-dd"));
  const [appliedChangeType, setAppliedChangeType] = useState<string>("all");
  const [appliedSystemFilter, setAppliedSystemFilter] = useState<SystemFilter>(SystemFilter.ALL);
  const [appliedUserEmail, setAppliedUserEmail] = useState<string>("");

  // Temporary filter states (for the form)
  const [dateTo, setDateTo] = useState<string>(DateTime.local().toFormat("yyyy-MM-dd"));
  const [changeType, setChangeType] = useState<string>("all");
  const [systemFilter, setSystemFilter] = useState<SystemFilter>(SystemFilter.ALL);
  const [userEmail, setUserEmail] = useState<string>("");

  const systemOptions = [
    { label: t("filter.system all"), value: SystemFilter.ALL },
    { label: t("filter.system google"), value: SystemFilter.GOOGLE },
    { label: t("filter.system meta"), value: SystemFilter.META },
  ];

  const changeTypeOptions = [
    { label: t("filter.change type.all"), value: "all" },
    { label: t("filter.change type.CampaignOptimization"), value: AuditLogsT.CampaignOptimizationT },
    { label: t("filter.change type.ChangeAudienceSignals"), value: AuditLogsT.ChangeAudienceSignalsT },
    { label: t("filter.change type.ChangeBiddingScheme"), value: AuditLogsT.ChangeBiddingSchemeT },
    { label: t("filter.change type.ChangeCampaignState"), value: AuditLogsT.ChangeCampaignStateT },
    { label: t("filter.change type.ChangeExtendedConversions"), value: AuditLogsT.ChangeExtendedConversionsT },
    { label: t("filter.change type.ChangeMonthlyBudget"), value: AuditLogsT.ChangeMonthlyBudgetT },
    { label: t("filter.change type.ChangeOrganizationDesignColor"), value: AuditLogsT.ChangeOrganizationDesignColorT },
    { label: t("filter.change type.CreditTransaction"), value: AuditLogsT.CreditTransactionT },
    { label: t("filter.change type.ExcludeCategories"), value: AuditLogsT.ExcludeCategoriesT },
    { label: t("filter.change type.PublishAssetLine"), value: AuditLogsT.PublishAssetLineT },
    { label: t("filter.change type.SelectCategories"), value: AuditLogsT.SelectCategoriesT },
    { label: t("filter.change type.SelectExcludedProductFlags"), value: AuditLogsT.SelectExcludedProductFlagsT },
    { label: t("filter.change type.TarifChange"), value: AuditLogsT.TarifChangeT },
  ];

  const getAdSystemForQuery = (filter: SystemFilter): AdSystemT | undefined => {
    if (filter === SystemFilter.GOOGLE) {
      return AdSystemT.GoogleAdsT;
    }
    if (filter === SystemFilter.META) {
      return AdSystemT.MetaAdsT;
    }
    return undefined;
  };

  const getChangeTypeForQuery = (type: string): AuditLogsT | undefined => {
    if (type === "all") {
      return undefined;
    }
    return type as AuditLogsT;
  };

  const toRACDateValue = (date: string | null | undefined): CalendarDate | null | undefined => {
    if (date === null) {
      return null;
    }

    if (date === undefined) {
      return undefined;
    }

    try {
      return parseDate(date);
    } catch {
      return null;
    }
  };

  const variables = {
    limit,
    page,
    dateTo: appliedDateTo,
    type: getChangeTypeForQuery(appliedChangeType),
    adSystem: getAdSystemForQuery(appliedSystemFilter),
    userEmail: appliedUserEmail || undefined,
  };

  const { data, loading } = useAuditLogsQuery({
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const submitFilter = () => {
    setAppliedDateTo(dateTo);
    setAppliedChangeType(changeType);
    setAppliedSystemFilter(systemFilter);
    setAppliedUserEmail(userEmail);
    setPage(1);
  };

  const clearFilter = () => {
    const today = DateTime.local().toFormat("yyyy-MM-dd");
    setDateTo(today);
    setChangeType("all");
    setSystemFilter(SystemFilter.ALL);
    setUserEmail("");
    setAppliedDateTo(today);
    setAppliedChangeType("all");
    setAppliedSystemFilter(SystemFilter.ALL);
    setAppliedUserEmail("");
    setPage(1);
  };

  if (loading) {
    return <SmallLoader />;
  }

  const items = data?.organization?.auditLogs ?? [];
  const totalItems = data?.organization?.auditLogsCount || 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / limit) : 0;

  const maxDate = DateTime.local().toFormat("yyyy-MM-dd");

  return (
    <div>
      <div>
        <div style={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "14px" }}>
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("filter.date to")}</div>
            <ExtendedDateField
              label={t("filter.date to")}
              maxValue={toRACDateValue(maxDate) ?? undefined}
              value={dateTo}
              labelHidden
              onChange={(value) => value && setDateTo(value)}
            />
          </div>
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("filter.change type label")}</div>
            <SingleSelectField
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              label={t("filter.change type label")}
              options={changeTypeOptions}
              value={changeType}
              labelHidden
              onChange={(value) => value && setChangeType(value)}
            />
          </div>
          {hasMetaCampaignsEnabled && (
            <div className="performance-stats-filter-item" style={{ flex: 1 }}>
              <div>{t("filter.system")}</div>
              <SingleSelectField
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                label={t("filter.system")}
                options={systemOptions}
                value={systemFilter}
                labelHidden
                onChange={(value) => value && setSystemFilter(value as SystemFilter)}
              />
            </div>
          )}
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("filter.user email")}</div>
            <TextField
              label={t("filter.user email")}
              value={userEmail}
              width="full"
              labelHidden
              onChange={setUserEmail}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexDirection: "row",
            margin: "24px 0 36px",
          }}
        >
          <Button size="sm" type="button" variant="action" onClick={submitFilter}>
            {t("filter.button")}
          </Button>
          <IconButton
            icon="shp-cross"
            size="sm"
            style={{ marginLeft: "10px" }}
            type="button"
            variant="primary"
            onClick={clearFilter}
          />
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: "128px 0", textAlign: "center" }}>
          <span
            style={{ fontSize: "28px", color: "var(--app-text-muted)", fontFamily: "Inter Display, Arial, sans-serif" }}
          >
            {t("empty state")}
          </span>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t("table header.date")}</TableHeaderCell>
                <TableHeaderCell>{t("table header.change")}</TableHeaderCell>
                <TableHeaderCell>{t("table header.system")}</TableHeaderCell>
                <TableHeaderCell>{t("table header.user")}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((auditLog) => (
                <TableRow key={auditLog.id}>
                  <TableCell>{formatTime(auditLog.createdAt)}</TableCell>
                  <TableCell>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span dangerouslySetInnerHTML={{ __html: auditLog.message }} />
                      {(auditLog.assetLine?.imageSuggestion || auditLog.assetLine?.uploadedImage) && (
                        <div className="AuditLog-image">
                          {auditLog.assetLine?.imageSuggestion && (
                            <ImageSuggestion scale={0.5} {...auditLog.assetLine.imageSuggestion} />
                          )}
                          {auditLog.assetLine?.uploadedImage && (
                            <UploadedImageSuggestion {...auditLog.assetLine.uploadedImage} />
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {auditLog.adSystem === AdSystemT.GoogleAdsT && t("filter.system google")}
                    {auditLog.adSystem === AdSystemT.MetaAdsT && t("filter.system meta")}
                  </TableCell>
                  <TableCell>{auditLog.userEmail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Pagination
            jump={
              totalPages > 1 ? (
                <Pagination.Jump
                  label={tPagination("show page")}
                  totalPages={totalPages}
                  getLinkProps={(_page) => ({
                    onClick: () => setPage(_page),
                    href: `javascript:void(0)`,
                  })}
                />
              ) : null
            }
            limits={
              <Pagination.Limits
                itemsPerPage={limit}
                label={tPagination("items per page")}
                onChange={(value) => {
                  setLimit(value as ItemsPerPage);
                  localStorage.setItem("shoptet_audit_logs_items_per_page", value.toString());
                  setPage(1);
                }}
              />
            }
            pages={
              <Pagination.Pages
                nextPageUrl={totalPages > 1 ? "javascript:void(next)" : null}
                pageCurrent={page}
                pagesTotal={totalPages}
                previousPageUrl={totalPages > 1 ? "javascript:void(prev)" : null}
                getLinkProps={(_page) => ({
                  onClick: () => {
                    let desiredPage = _page;
                    if (_page === "javascript:void(prev)") {
                      desiredPage = page - 1 < 1 ? 1 : page - 1;
                    }
                    if (_page === "javascript:void(next)") {
                      desiredPage = page + 1 > totalPages ? totalPages : page + 1;
                    }
                    setPage(desiredPage as number);
                  },
                  href: `javascript:void(0)`,
                })}
              />
            }
            summary={
              totalItems > 0
                ? asRichText(
                    <Trans
                      components={{ strong: <strong /> }}
                      i18nKey="displaying items total"
                      t={tPagination}
                      values={{ totalItems }}
                    />
                  )
                : undefined
            }
          />
        </>
      )}
    </div>
  );
};
